import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button } from "reactstrap";
import { useTranslation } from "react-i18next";
import {
  TENANT_AUTH_STORAGE_KEY,
  getLoggedinUser,
} from "../../helpers/api_helper";
import { getMe } from "../../helpers/fakebackend_helper";

/**
 * Banner de sessão de suporte (impersonation).
 * Por quê no shell tenant: SA continua em sa_authUser; o operador precisa
 * de um lembrete visível e um botão para voltar sem confundir com logout.
 */
const ImpersonationBanner = () => {
  const { t } = useTranslation();
  const [active, setActive] = useState(false);
  const [endsAt, setEndsAt] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const session = getLoggedinUser();
    if (!session) {
      setActive(false);
      return;
    }

    if (session.impersonation === true) {
      setActive(true);
      if (session.impersonation_expires_at) {
        setEndsAt(String(session.impersonation_expires_at));
      }
      const name = session.tenant?.name;
      if (typeof name === "string" && name.trim() !== "") {
        setTenantName(name.trim());
      }
    }

    let cancelled = false;
    getMe()
      .then((res: {
        success?: boolean;
        data?: {
          impersonation?: {
            active?: boolean;
            expires_at?: string | null;
          } | null;
          tenant?: { name?: string } | null;
        };
      }) => {
        if (cancelled) {
          return;
        }
        const imp = res?.data?.impersonation;
        if (imp?.active) {
          setActive(true);
          if (imp.expires_at) {
            setEndsAt(imp.expires_at);
          }
          const name = res?.data?.tenant?.name;
          if (typeof name === "string" && name.trim() !== "") {
            setTenantName(name.trim());
          }
          return;
        }
        if (session.impersonation !== true) {
          setActive(false);
        }
      })
      .catch(() => {
        // Mantém flag local se a API falhar.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Countdown a cada 30s — evita timer 1s barulhento.
  useEffect(() => {
    if (!active || !endsAt) {
      return;
    }
    const id = window.setInterval(() => setNowMs(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, [active, endsAt]);

  const remainingLabel = useMemo(() => {
    if (!endsAt) {
      return null;
    }
    const diffMs = new Date(endsAt).getTime() - nowMs;
    if (Number.isNaN(diffMs)) {
      return null;
    }
    if (diffMs <= 0) {
      return t("sa.impersonation.expired");
    }
    const mins = Math.max(1, Math.ceil(diffMs / 60000));
    return t("sa.impersonation.remainingMinutes", { minutes: mins });
  }, [endsAt, nowMs, t]);

  if (!active) {
    return null;
  }

  const endSession = () => {
    const session = getLoggedinUser();
    const returnPath =
      (session?.impersonation_return_path as string | undefined) ||
      (session?.tenant?.id
        ? `/super-admin/tenants/${session.tenant.id}`
        : "/super-admin/tenants");
    sessionStorage.removeItem(TENANT_AUTH_STORAGE_KEY);
    window.location.href = returnPath;
  };

  return (
    <Alert
      color="warning"
      className="mb-0 rounded-0 d-flex flex-wrap justify-content-center align-items-center gap-2 py-2 px-3"
    >
      <span>
        {tenantName
          ? t("sa.impersonation.bannerWithTenant", { name: tenantName })
          : t("sa.impersonation.banner")}
        {remainingLabel ? ` ${remainingLabel}` : ""}
      </span>
      <Button color="dark" size="sm" onClick={endSession}>
        {t("sa.impersonation.endSession")}
      </Button>
    </Alert>
  );
};

export default ImpersonationBanner;
